import type { QuizAttemptPrivate, QuizPrivate, QuizSection } from '@repo/types/quiz';
import type { UseQueryResult } from '@tanstack/react-query';

import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { resolveDestinationSection } from '@repo/lib/quiz/resolveDestinationSection';
import { useQueries } from '@tanstack/react-query';

import { useErrorMessage } from '#error-message/useErrorMessage';

import { QuizKey } from './keys';
import { QuizService } from './quizService';

export function useGetQuizWithAttempt(
  quizId: string,
  attemptId: string,
  sectionNumber: number,
  redirect: (quizId: string, attemptId: string, destination: number | null) => void,
  syncWithAttemptData: (content: QuizSection[], attempt: QuizAttemptPrivate) => void
) {
  const combineFn = (results: [UseQueryResult<QuizPrivate, Error>, UseQueryResult<QuizAttemptPrivate, Error>]) => {
    const [quizQuery, attemptQuery] = results;
    const isPending = quizQuery.isPending || attemptQuery.isPending;
    const isError = quizQuery.isError || attemptQuery.isError;
    const error = quizQuery.error || attemptQuery.error;
    const isSuccess = quizQuery.isSuccess && attemptQuery.isSuccess;
    const data = isSuccess ? { quiz: quizQuery.data, attempt: attemptQuery.data } : undefined;

    if (isSuccess && data) {
      const { destination } = resolveDestinationSection({
        quiz: data.quiz,
        attempt: data.attempt,
        sectionNumber,
      });

      if (!data.quiz.freeNavigation && destination !== null && destination !== sectionNumber) {
        redirect(quizId, attemptId, destination);
      }
      syncWithAttemptData(data.quiz.content, data.attempt);
    }

    return {
      data,
      isPending,
      isError,
      isSuccess,
      error,
    };
  };

  const queries = useQueries({
    queries: [
      {
        queryKey: [QuizKey.QUIZ, quizId],
        queryFn: () => QuizService.getQuiz(quizId).then((response) => response.data),
        enabled: !!quizId,
      },
      {
        queryKey: [QuizKey.QUIZ_ATTEMPT, quizId, attemptId],
        queryFn: () => QuizService.getAttempt(quizId, attemptId).then((response) => response.data),
        enabled: !!quizId && !!attemptId,
      },
    ],
    combine: (results) => combineFn(results),
  });

  const errorMessage = useErrorMessage(queries.error, quizErrorMessages);

  return {
    ...queries,
    errorMessage,
  };
}
